import { fields } from "@omnia/exports/fields"
import { arrayMin, createLookup, getDistinctValues, isArrayEmpty, partition, selectMany, sum } from "../utils/array"
import { checkDateIntersection } from "../utils/date"
import { isArray, isObject, sortDates } from "../utils/utils"

interface SourceCropping {

    sourceId: any,

    cropId: string | number,
    perennial: boolean,
    year: number,
    startDate: Date,
    endDate: Date,
    area: number
    destroyed: boolean,

}

type LocalCropping = SourceCropping & {
    id: number,
    source: any
}

interface HistoryUnit {
    fieldName: string,
    firstId: number
    firstYear: number
    lastId: number
    lastYear: number
    cropping: LocalCropping[]
    startDate: Date
    endDate: Date
    area: number
    perennial: {
        destroyed: boolean
    }
}

interface HistoryUnitRelation {
    prev: HistoryUnit
    next: HistoryUnit
}

interface Relation {
    prev: number
    next: number
}

export interface CroppingHistory<T> {
    cropping: T,
    prev: T[]
    next: T[]
}

interface SearchContext {
    units: HistoryUnit[]
    field: Field
    hasPrevious(unit: HistoryUnit): boolean
    getNextUnits(): HistoryUnit[]
}

type RelateStep = ((current: HistoryUnit, context: SearchContext) => HistoryUnitRelation[])

type Index = { [key: number]: boolean }

interface Field {
    fieldName: string
    fieldArea: number
}

export function getFieldHistory<T>(cropping: T[], field: Field, selector: (c: T) => SourceCropping) {

    const localCropping = cropping.map((c, index) => ({

        ...selector(c),

        id: (index + 1),
        source: c

    }))

    const units = getHistoryUnits(localCropping, field?.fieldName)

    // console.log("getFieldHistory: units", units.map(u => ({ field: u.fieldName, cropping: u.cropping.map(i => i.sourceId) })))

    const steps = [
        [getYearBasedDefaultRelations],
        [getSingleUnitRelations],
    ]

    const relations = getInnerRelations(units).concat(getRelations(units, field, steps))

    // console.log("getFieldHistory: relations", relations)

    const history = getResults<T>(localCropping, relations)

    return history

}

function getRelations(units: HistoryUnit[], field: Field, steps: RelateStep[][]): Relation[] {

    units = units.sort((a, b) => sortDates(b.endDate, a.endDate))

    const prevIndex = {} as Index
    const nextIndex = {} as Index

    const relations = steps.map(stepGroup => {

        const relations = units
            .map((u, index) => {

                if (nextIndex[u.lastId]) {
                    return []
                }

                const context = getSearchContext(units, field, prevIndex, index)

                const results = findRelations(u, context, stepGroup) || []

                const relations = results.map(r => ({ prev: r.prev.lastId, next: r.next.firstId }))

                relations.forEach(n => {
                    nextIndex[n.prev] = true
                    prevIndex[n.next] = true
                })


                return relations

            })

        return selectMany(relations)

    })

    return selectMany(relations)

}


function findRelations(current: HistoryUnit, context: SearchContext, steps: RelateStep[]) {

    for (const step of steps) {

        const rels = step(current, context)

        if (!isArrayEmpty(rels)) {
            return rels
        }

    }

    return []

}

function getYearBasedDefaultRelations(current: HistoryUnit, context: SearchContext) {

    const year = current.lastYear

    const next = context.units
        .filter(u => u.firstYear == (year + 1))
        .filter(u => u.startDate > current.startDate)

    if (next.length == 1) {
        return [{ prev: current, next: next[0] }]
    }

    // 220110 - Usersnap 2400
    // Allow multiple successors only if there is a single crop on current.lastYear
    // 250506 - Usersnap 6243 / DevOps 3761
    // But don't allow any successor, make sure that they all fit in the field's area
    if ((next.length > 1) && checkTotalArea(next)) {

        const multiplePrev = context.units.some(u =>
            (u !== current)
            && (u.firstYear >= current.lastYear)
            && (u.lastYear <= current.lastYear)
        )

        if (!multiplePrev) {
            return next.map(next => ({
                prev: current,
                next
            }))
        }

    }


    function checkTotalArea(units: HistoryUnit[]) {

        const fieldArea = context.field?.fieldArea || 0

        if (fieldArea) {
            const totalArea = sum(units, n => n.area) || 0
            const r = totalArea / fieldArea
            // Completely arbitrary: allow up to 110% of the field area
            return (r <= 1.1)
        }

        return true
    }


}

function getSingleUnitRelations(current: HistoryUnit, context: SearchContext) {

    var rels = run() || []

    return rels.filter(checkValidRelation)

    ///

    function run() {

        const prev = [current]
            .concat(getConcurrentCrops(current, context.units))
            .filter(c => (!c.perennial) || (c.perennial.destroyed))
            .sort((a, b) => b.area - a.area)

        if (isArrayEmpty(prev)) {
            return null
        }

        const earliestEnd = arrayMin(prev, c => c.endDate)

        let next = context.getNextUnits()
            .filter(n => n.startDate >= earliestEnd)
            .filter(n => !context.hasPrevious(n))
            .filter(n => prev.every(p => p !== n))
            .sort((a, b) => b.area - a.area)

        if (prev.length == 1) {
            return next.map(n => ({ prev: prev[0], next: n }))
        }

        if (next.length > 0) {

            const earliestNext = (next.sort((a, b) => sortDates(a.startDate, b.startDate)))[0]

            next = [earliestNext].concat(getConcurrentCrops(earliestNext, next))

            if (next.length == 1) {
                return prev.map(p => ({ prev: p, next: next[0] }))
            }

            const largestPrev = (prev.sort((a, b) => b.area - a.area))[0]

            if (largestPrev) {
                return next.map(n => ({ prev: prev[0], next: n }))
            }

        }

    }

}

function getConcurrentCrops(from: HistoryUnit, units: HistoryUnit[]) {

    const { startDate: start, endDate: end } = from

    return units.filter(u =>
        (u != from)
        && ((start < u.endDate) && (end > u.startDate))
    )

}

function checkValidRelation(rel: HistoryUnitRelation) {
    const { prev, next } = rel
    return !checkDateIntersection(prev.startDate, prev.endDate, next.startDate, next.endDate, true)
}

function getResults<T>(cropping: LocalCropping[], relations: Relation[]): CroppingHistory<T>[] {

    const prevLookup = createLookup(relations, i => i.prev)
    const nextLookup = createLookup(relations, i => i.next)

    const find = (ids: number[]) => ids.map(id => cropping.find(c => c.id == id)).filter(c => !!c).map(c => c.source as T)

    return cropping.map(c => {

        const prevIds = getDistinctValues((nextLookup[c.id] || []).map(p => p.prev), id => id)
        const nextIds = getDistinctValues((prevLookup[c.id] || []).map(n => n.next), id => id)

        return {

            cropping: c.source as T,

            prev: find(prevIds),
            next: find(nextIds),

        }

    })

}

function getSearchContext(units: HistoryUnit[], field: Field, prevIndex: Index, currentIndex: number): SearchContext {

    return {
        units,
        field,
        hasPrevious: (unit: HistoryUnit) => !!prevIndex[unit.firstId],
        getNextUnits: () => units.filter((u, index) => (index < currentIndex))
    }

}

function getInnerRelations(units: HistoryUnit[]): Relation[] {

    return selectMany(units.map(u => u.cropping.map((c, index) => {

        if (index >= (u.cropping.length - 1)) {
            return null
        }

        const next = u.cropping[index + 1]

        return {
            prev: c.id,
            next: next.id
        }

    }).filter(i => !!i)))

}

function getHistoryUnits(cropping: LocalCropping[], fieldName: string): HistoryUnit[] {

    cropping = cropping.filter(checkValidCropping)

    const [perennial, annual] = partition(cropping, p => p.perennial)

    const units1 = annual.map(a => [a])
        .concat(getPerennialGroups(perennial))
        .map(g => createHistoryUnit(g, fieldName))

    const units2 = mergeUnitsByArea(units1, 1)

    return units2

}

function createHistoryUnit(cropping: LocalCropping[], fieldName: string): HistoryUnit {

    const g = cropping

    const sorted = g.sort((a, b) => a.year - b.year)
    const first = sorted[0]
    const last = sorted[sorted.length - 1]

    const perennial = last.perennial
        ? ({ destroyed: last.destroyed })
        : null

    return {
        fieldName,
        firstId: first.id,
        firstYear: first.year,
        lastId: last.id,
        lastYear: last.year,
        cropping: sorted,
        perennial,
        startDate: first.startDate,
        area: last.area,
        endDate: last.endDate
    }

}

function mergeUnitsByArea(units: HistoryUnit[], iteration: number) {

    if (isArrayEmpty(units)) {
        return units
    }

    const initLength = units.length

    units = units.sort((a, b) => sortDates(a.startDate, b.startDate))

    // console.log("mergeUnitsByArea starts", iteration, units.map(unitToString))

    const results = [] as HistoryUnit[]

    while (units.length) {

        const u = units[0]
        units = units.slice(1)

        const others = units.filter(n => n.startDate >= u.endDate)
        const cc = getNextRank(others).filter(n => n.area == u.area)

        // console.log("\t", unitToString(u), cc.map(unitToString))

        if (!cc.length) {

            results.push(u)

        }
        else {

            const same = cc[0]

            const merged = {
                ...u,
                lastId: same.lastId,
                lastYear: same.lastYear,
                endDate: same.endDate,
                cropping: u.cropping.concat(same.cropping)
            }

            units = [merged].concat(units.filter(r => (r != same)))

        }

    }

    // console.log("mergeUnitsByArea ends", iteration, results.map(unitToString))

    return (results.length == initLength)
        ? results
        : mergeUnitsByArea(results, iteration + 1)

}

function getNextRank(units: HistoryUnit[]) {

    if (isArrayEmpty(units)) {
        return units
    }

    const initState = {
        matches: [units[0]],
        maxDate: units[0].endDate
    }

    return units.slice(1).reduce((s, u) => {

        if (u.startDate <= s.maxDate) {
            s.matches.push(u)
            s.maxDate = (s.maxDate > u.endDate ? u.endDate : s.maxDate)
        }

        return s

    }, initState).matches

}

function getPerennialGroups(cropping: LocalCropping[]): LocalCropping[][] {

    const groups = [] as LocalCropping[][]

    const duration = (c: LocalCropping) => Math.abs(c.endDate.valueOf() - c.startDate.valueOf())

    cropping = cropping
        .sort((a, b) => (a.year - b.year)
            || sortDates(a.startDate, b.startDate)
            || (duration(b) - duration(a)))

    while (cropping.length) {

        const group = getPerennialGroup(cropping)

        groups.push(group)
        group.forEach(i => cropping.remove(c => c == i))

    }

    return groups

}

const wellKnownPerennialCrops = new Set<string | number>([
    53, //Grass Margin
    115, //Mid Tier Stewardship
    112, //Woodland
    91, //EFA
    67, //Game Cover
    113, //Non-farmed Area
    102, //Wild Bird mix
    98, //Pollen/Nectar
    100, //Floral Margin
    116, //Higher Tier Stewardship
    354, // SFI crop action
])


function getPerennialGroup(cropping: LocalCropping[]): LocalCropping[] {

    let lastIndex = 0
    const group = [cropping[0]]

    while (lastIndex < cropping.length) {

        const last = cropping[lastIndex]

        let candidates = cropping
            .map((c, index) => ({
                cropping: c,
                index,
                ok: (index > lastIndex) && checkPerennialSequence(last, c, i => i)
            }))
            .filter(i => i.ok)


        if (candidates.length > 1) {
            candidates = candidates.filter(c => c.cropping.area == last.area)
        }
        // If there is a single candidate, and it matches a few well-known perennial crops
        // (see the set above) which are likely to exist for consecutive years,
        // then it is retained automatically without considering its area
        else if (!wellKnownPerennialCrops.has(last.cropId)) {
            candidates = []
        }

        if (!candidates.length) {
            break
        }

        const match = candidates[0]

        group.push(match.cropping)
        lastIndex = match.index

    }

    return group

}

export function checkPerennialSequence<T>(prevList: T | T[], nextList: T | T[], selector: (a: T) => {
    cropId: number | string,
    year: number,
    startDate: Date,
    endDate: Date,
    destroyed: boolean
}) {

    const prev = getSingle(prevList)
    if (!prev) return false

    const next = getSingle(nextList)
    if (!next) return false

    if (prev.cropId != next.cropId) return false
    if (prev.year != (next.year - 1)) return false

    if (!prev.destroyed) return true

    const dateDiff = next.startDate.valueOf() - prev.endDate.valueOf()

    // 2 days
    return (dateDiff < (2 * (1000 * 60 * 60 * 24)))


    function getSingle(a: T | T[]) {

        const single = isArray(a)
            ? ((a.length == 1) ? a[0] : null)
            : a

        if (isObject(single)) {
            return selector(single as T)
        }

    }

}

function checkValidCropping(c: LocalCropping) {

    if (c.startDate >= c.endDate) {
        return false
    }

    return true

}
